import { primitives } from '@limitless-minds/ui-styles'

/**
 * The semantic roles this application paints, light and dark.
 *
 * A role answers the question a design-system token cannot: what is this colour FOR. The palette names
 * luminance - `neutralDark400` says how dark a colour is, not what it does - so choosing one means
 * knowing what sits underneath, and with two colour schemes that is a different answer per mode.
 * Material 3's `on` prefix encodes the pairing: `onX` is defined in relation to `X`, so it inverts with it
 * by construction and an ink cannot be named without naming its surface.
 *
 * ## Thirteen roles, not the app's thirty-eight
 *
 * This is a pre-login application of six screens: a form, a stepper, a link, an error banner and a
 * password strength meter. The roles absent here are absent because nothing paints them - there is no
 * sunken surface, no overlay, no tooltip and no fixed brand chrome. Declaring one anyway puts rows in the
 * contrast matrix measuring colours no member ever sees, which is a gate reporting green over data nobody
 * uses.
 *
 * The rule cuts both ways, and that is the part worth stating: a role set is wrong when it holds what
 * nobody paints AND when it lacks what somebody does. There is no `outline` and no `onErrorContainer`
 * because nothing reaches for them; `success` and `warning` are here because the strength meter expresses
 * exactly those two states, and a set that made it name its own colours would be forcing the failure this
 * layer exists to prevent.
 *
 * The values are taken from the app frontend's table rather than decided again, so the two products agree
 * on what a surface and an ink are.
 *
 * IMPORTANT: nothing checks that they keep agreeing. The two role maps live in different repositories and
 * neither build can read the other, so a value moved in the app leaves this one behind silently. What
 * catches it is a probe that loads both origins in one browser and compares the computed colours; the day
 * a value has to be copied here by hand because this subset lacks the role, the layer belongs in
 * `ui-styles` instead.
 *
 * ## The primitives come from the package
 *
 * `ui-styles` exports the 35 raw values, so this file names hues rather than restating hexes. That leaves
 * the package as the single owner of what a colour IS, and this file as the owner of what it is FOR - the
 * split the whole layer exists for. A component reaches neither directly: `no-restricted-imports` keeps
 * `primitives` inside this directory, because a component that names a hue has skipped the step where the
 * contrast is decided.
 */

export type RoleName =
  // Surfaces. `surface` is the canvas the header and footer sit on. `surfaceVeiled` is what almost every
  // pixel of content actually lands on: a 90% veil over the halftone, which at that alpha replaces the
  // canvas rather than tinting it, so it IS a surface and not a decoration. The two veils are
  // complementary by breakpoint, so exactly one is always on beneath the content. `surfaceRaised` is what
  // MUI paints a `Paper` with through the bridge.
  | 'surface'
  | 'surfaceVeiled'
  | 'surfaceRaised'
  // Ink. One ramp: all three tints clear AA over both surfaces in both modes.
  | 'onSurface'
  | 'onSurfaceVariant'
  | 'onSurfaceMuted'
  // Border. `outlineVariant` is the low-emphasis line: a divider, and the strength meter's track. There
  // is no `outline` - a control border - because every control here comes from `ui-*` and draws its own.
  | 'outlineVariant'
  // Accent
  | 'primary'
  | 'onPrimary'
  // Feedback. `error` is the banner's glyph and the meter's lowest step; `warning` and `success` are the
  // steps above it. `errorContainer` is the banner's tinted box, whose ink is `onSurface` rather than a
  // tinted one - so there is no `onErrorContainer`.
  | 'error'
  | 'onError'
  | 'errorContainer'
  | 'success'
  | 'warning'

type PrimitiveName = keyof typeof primitives

/**
 * Both maps are the same required-field type, so a key missing from either one is a compile error - in
 * both directions, since an extra key is an excess property. It has to be a type rather than a test
 * because the merge that consumes these maps is shallow at one level: a key present in light and absent
 * from dark reads `undefined` through `theme.palette` while its CSS variable keeps the light fallback.
 * Two different wrong answers, neither of which throws.
 */
export type RoleMap = Record<RoleName, PrimitiveName>

export const light: RoleMap = {
  surface: 'halftone',
  // The composite of the veil over the halftone, to the nearest primitive, and chosen to sit on the
  // strict side of it: the real composite is `#FAFBFD` here and `#222B36` in dark, so `halftone` and
  // `slate800` each measure a hair LOWER than what the member sees. A gate that approximates a surface
  // has to approximate it pessimistically, or it grants headroom that is not there.
  surfaceVeiled: 'halftone',
  surfaceRaised: 'white',

  onSurface: 'slate800',
  onSurfaceVariant: 'slate600',
  // `slate500` rather than the `slate400` these sites paint today. That value measures 4.02 against the
  // page, under the 4.5 text minimum, so muted ink at its current value fails AA in the light theme
  // before dark mode is considered. `slate500` measures 5.40.
  onSurfaceMuted: 'slate500',

  outlineVariant: 'grey100',

  // `blue600`, which is what the app frontend's `primary` resolves to, so a link reads the same in both
  // products. The step below it, `blue700`, is the filled button's blue: WEBAPP-648 sent every text use
  // there because the scale jumped straight to it, and that left links a dark navy in both applications.
  //
  // IMPORTANT: this value is a copy, and nothing in either build compares the two. It was `blue700` for
  // three hours because the app moved and this did not - measured, and caught by reading rather than by a
  // gate. Whoever moves an accent moves both, or the two products drift apart in silence.
  primary: 'blue600',
  onPrimary: 'white',

  error: 'red700',
  onError: 'white',
  errorContainer: 'red50',
  success: 'green700',
  warning: 'amber800',
}

export const dark: RoleMap = {
  surface: 'navy800',
  surfaceVeiled: 'slate800',
  surfaceRaised: 'navy700',

  onSurface: 'white',
  onSurfaceVariant: 'grey100',
  onSurfaceMuted: 'grey200',

  outlineVariant: 'navy600',

  primary: 'blue300',
  // Navy, not white. White on `blue300` measures 2.30, which is the failure this prefix exists to make
  // unwritable: the ink is chosen against its own surface, and here that surface is a light blue.
  onPrimary: 'navy900',

  error: 'red400',
  onError: 'navy900',
  errorContainer: 'red950',
  success: 'green400',
  warning: 'amber500',
}

/**
 * Which surface each ink is declared to sit on, with the minimum it owes there.
 *
 * This is the data `verify:contrast` turns into a matrix, and writing it down is the point: the pairing
 * cannot be inferred from source, because a colour reaches its background through layout rather than
 * through the expression that names it.
 *
 * `onSurface` over `errorContainer` is the error banner, whose ink is the ordinary body colour rather
 * than a tinted one - the row exists because the box is tinted even though the ink is not.
 *
 * `outlineVariant` never appears as the INK of a row, and that is an exemption rather than an oversight:
 * a divider and a progress track are neither text nor a state indicator, so SC 1.4.11 does not reach
 * what they measure against the surface behind them - 1.23 on the card in light and 1.16 in dark. What
 * the criterion does reach is what sits ON the track, and those three rows are declared.
 */
// IMPORTANT: exported with no importer, and it has to stay that way. `read-role-layer.mjs` reads this out
// of the file's TEXT and anchors on the exported declaration, so dropping the keyword makes
// `verify:contrast` report that it cannot read the matrix at all - and it would also become an unused
// local that the type check rejects.
export const PAIRS: Array<[ink: RoleName, surface: RoleName, minimum: number]> = [
  ['onSurface', 'surface', 4.5],
  ['onSurface', 'surfaceVeiled', 4.5],
  ['onSurface', 'surfaceRaised', 4.5],
  ['onSurfaceVariant', 'surface', 4.5],
  ['onSurfaceVariant', 'surfaceVeiled', 4.5],
  ['onSurfaceVariant', 'surfaceRaised', 4.5],
  ['onSurfaceMuted', 'surface', 4.5],
  ['onSurfaceMuted', 'surfaceVeiled', 4.5],
  ['onSurfaceMuted', 'surfaceRaised', 4.5],
  ['onPrimary', 'primary', 4.5],
  ['onError', 'error', 4.5],
  // The accents also land as ink directly on a surface, so each owes the text minimum there too.
  ['primary', 'surface', 4.5],
  ['primary', 'surfaceVeiled', 4.5],
  ['primary', 'surfaceRaised', 4.5],
  ['error', 'surface', 4.5],
  ['error', 'surfaceVeiled', 4.5],
  ['error', 'surfaceRaised', 4.5],
  // The error banner: an untinted ink and the alert glyph, both on the tinted box. The glyph owes 3.0
  // rather than 4.5 because it is an icon.
  ['onSurface', 'errorContainer', 4.5],
  ['error', 'errorContainer', 3.0],
  // The strength meter's three steps, each against the TRACK rather than the card behind it. That is the
  // pair SC 1.4.11 reaches for a state indicator - what surrounds the mark, not what is further back -
  // and it is the one a colour picked to look right on a white page silently misses.
  ['error', 'outlineVariant', 3.0],
  ['warning', 'outlineVariant', 3.0],
  ['success', 'outlineVariant', 3.0],
]

/** Every role resolved to its hex, for one mode. */
export type RoleColors = Record<RoleName, string>

/**
 * The role-to-hex hop, in one place.
 *
 * It lives here because this is the only module that needs the VALUE of a primitive - the palette group
 * and the MUI slot bridge both consume roles that are already resolved, so they get this map rather than
 * repeating the lookup. That keeps the package import with a single consumer, which is what makes the
 * direction the ESLint rule enforces outside this directory true inside it as well.
 */
export const roleColors = (roles: RoleMap): RoleColors =>
  Object.fromEntries(Object.entries(roles).map(([role, primitive]) => [role, primitives[primitive]])) as RoleColors
